<template>
    <VueMultiselect
        v-model="selection"
        :options="options"
        :name="name"
        :label="field"
        v-bind="$attrs"
        :show-labels="showLabels"
        :disabled="disabled"
        track-by="_localId"
        :loading="loadingOptions"
        :placeholder="placeholder" 
        :multiple="true"
        :max="maxItems"
        :internal-search="false" 
        :clear-on-select="true" 
        :close-on-select="true" 
        :max-height="600" 
        :show-no-results="true" 
        :hide-selected="true" 
        :block-keys="['Delete']"
        :taggable="isTaggable"
        :tag-placeholder="addNewPlaceholder"
        tag-position="bottom"
        class="item-autocomplete-multiselect"
        @tag="addNew"
        @search-change="getOptions"
        @input="onInput"
    >  <!-- we have block-keys="['Delete']" to block the deletion on backspace in search box... its annoying, and for parts/items, I don't think its needed -->
        <template #tag="{ option, remove }">
            <div>
                <div :id="`${multiselectId}_item_${option._localId}`" class="item-autocomplete-tag">
                    <!-- TODO - replace these with hoverable chips that show popover of more data later -->
                    <div class="tag-text">
                        {{ option.display_name || option.part_number || option.name }}
                        <b-badge v-if="!option.id" variant="soft-success" class="ml-1 text-uppercase font-weight-bold" style="">
                            New
                        </b-badge>
                    </div>
                    <button v-if="!disabled" class="tag-remove" @click="remove(option)">
                        <b-icon icon="x"></b-icon>
                    </button>
                </div>
                <b-popover 
                    :target="`${multiselectId}_item_${option._localId}`" 
                    triggers="hover" 
                    placement="top"
                    :title="option.display_name || option.part_number || option.name"
                >
                    <!-- <template #title>{{ option.display_name || option.part_number || option.name }}</template> -->
                    <dl class="form-row mb-0" style="min-width: 260px;">
                        <dt v-if="option.name" class="col-4">Name</dt>
                        <dd v-if="option.name" class="col-8">{{ option.name }}</dd>
                        <dt v-if="option.part_number" class="col-4">Part Number</dt>
                        <dd v-if="option.part_number" class="col-8">{{ option.part_number }}</dd>
                        <dt v-if="option.description" class="col-4">Description</dt>
                        <dd v-if="option.description" class="col-8">{{ option.description }}</dd>
                    </dl>
                    <div class="text-right">
                        <b-link v-if="option.url" :href="option.url" target="_blank" class="text-decoration-none">
                            View Details
                            <b-icon icon="box-arrow-up-right" class="ml-1"></b-icon>
                        </b-link>
                    </div>
                    
                </b-popover>
            </div>
        </template>
        <template #option="slotProps">
            <div class="">
                <div v-if="slotProps.option.id" :key="`existingOption-${slotProps.option.id}`" class="d-flex gap-3 font-weight-bold">
                    <span class="font-weight-bold">{{ slotProps.option.part_number || slotProps.option.name }}</span>
                    <!-- show part_number first, fallback to name... then if you have both, show both -->
                    <span v-if="slotProps.option.part_number && slotProps.option.name" class="font-weight-bold">{{ slotProps.option.name }}</span>
                </div>
                <div v-if="!slotProps.option.id" key="newOption" class="font-weight-bold">
                    {{ slotProps.search }}
                    <span class="ml-2 text-uppercase font-weight-bold text-success">
                        +<span style="font-size: 80%;">ADD</span>
                    </span>
                </div>
                <div class="text-muted font-size-70">{{ slotProps.option.description }}</div>
            </div>
        </template>
        <template #clear>
            <div 
                class="multiselect__clear" 
                v-if="hasSelection" 
                @mousedown.prevent.stop="clearAll"
            ></div>
        </template>
        <template #noResult>
            <div>No items found.  Change search query.</div>
        </template>
        <template #noOptions>
            <div>Start typing a part number to search...</div>
        </template>
    </VueMultiselect>
</template>

<script>
import { suppliosApi } from '../../js/api-service';
// import PrimeAutoComplete from 'primevue/autocomplete';  // not using this... not customizable enough
import VueMultiselect from 'vue-multiselect'
//import { nanoid } from 'nanoid';
//import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';

export default {
    name: 'ItemAutocompleteMultiselect',
    components: {
        VueMultiselect,
    },
    props: {
        value: {  // v-model with parent - we report up using @input
            // pass in FULL OBJECTS -- NOT JUST IDs -- needed so we can deal with new/existing
            type: [Object, Array],
            required: false,
        },
        field: {  // either 'part_number' or 'name'
            type: String,
            default: 'part_number',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: '',  // name to use as the form input
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        required: {  // unless minItems is also set, this means minItems = 1
            type: Boolean,
            default: false, // ???
        },
        // you may? want to only enforce minItems if you have any value at all???  ignore if not required and no value?
        minItems: {  // only used if multiple = true - if this is non-zero, then 'required' is ignored
            type: Number,
            required: false,
        },
        maxItems: { // only used if multiple = true
            type: Number,
            required: false,
        },
        noAdd: {  // block adding new ones?  doing like this so default of false
            type: Boolean,
            default: false,   
        },
        addEager: {  // if true, we make API call to add new items immediately, otherwise we just pass up full objects for new ones, let parent handle adding them
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Search items...'
        },
        addNewPlaceholder: {
            type: String,
            default: 'Add this as new item',
        },
        showLabels: {  // show things like 'Press enter to select' on each option
            type: Boolean,
            default: false,
        },
        // other props will be passed through to multiselect component using v-bind="$attrs"
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            // MULTISELECT
            multiselectId: String(uniqueId()),  // make a uniqueId string, in case you have multiple on page - use in id for options if needed also
            loadingOptions: false,
            selection: [],  // our 'local' value -- ALWAYS AN ARRAY
            options: [],
        }
    },
    watch: {
        value: {
            immediate: true,
            deep: true,
            handler: function(value) {
                // incoming value could be single object (if not multiple=true), array of objects, or null
                console.log('in value watch handler', value);
                this.setSelection(value);  // sets this.selection
            }
        },
    },
    computed: {
        hasSelection() {
            return !!this.selection.length;
        },
        selectionCount() {
            return this.selection.length;
        },
        finalMinItems() {  // works for multiple or single
            const minItems = this.minItems ? Number(this.minItems) : 0;
            return this.required ? (minItems || 1) : minItems;
        },
        finalMaxItems() {
            // return undefined if not set, or 1 if single select
            return this.multiple ? this.maxItems : 1;
        },
        isValid() {
            // does it meet requirements of 'required', 'minItems', 'maxItems'
            return (this.selectionCount >= this.finalMinItems) && (this.selectionCount <= this.finalMaxItems);
        },
        selectedLocalIds() {
            // return array of JUST _localId for all selections -- always array if single or multiple
            return this.selection.map(s => s._localId);
        },
        isTaggable() {
            return !this.noAdd;
        },
        isMaxedOut() {
            return this.selectionCount 
        }
    },
    created() {
        // this.resetSelection();  // don't call this... do it as part of watcher on value
    },
    methods: {
        async getOptions(searchQuery) {
            // this gets called with empty string for searchQuery when dropdown is closed (only if clear-on-select is true?)
            // if that happens, just clear all options
            this.loadingOptions = true;
            if (!searchQuery) {
                this.options = [];  // clear options manually and done - do we need to keep existing selections in options?  should be no... next time we get new options we'll merge existing selection with new options
                this.loadingOptions = false;
                return;
            }
            // for now we just hit the normal list endpoint with custom 'page_size' and 'search' 
            const params = {
                page_size: 1000,
                search: searchQuery,
            };
            const axiosConfig = {params};
            let resp;
            try {
                resp = await suppliosApi.item.list(axiosConfig);
            } catch (error) {
                // just log it for now...
                console.error(error);
            }
            if (resp?.data) {
                const results = resp.data.results ?? [];  // its paginated
                // transform things at all???  add a local key to know which are existing vs. new
                // we need to track which things are selected, including any existing and new ones
                // multiselect component can do this for us using built-in track-by to know which ones are already selected, even after reload options!
                // we make unique keys using `${this.field}_${field_value}` -- unless it doesn't have field value like no part_number -- then we just use ID instead since that will always be unique, and consistent between reloads
                const fetchedOptions = results.map(i => this.makeLocalOption(i));
                // MUST COMBINE WITH EXISTING SELECTIONS FOR SELECTION TRACKING TO WORK PERFECTLY
                const fetchedLocalIds = fetchedOptions.map(fo => fo._localId);
                const existingSelections = this.selection.filter(sel => !fetchedLocalIds.includes(sel._localId));
                this.options = [...fetchedOptions, ...existingSelections];
            }
            this.loadingOptions = false;
        },
        makeLocalOption(optionObj) {
            // pass in some option object from API or from parent, this adds the appropriate _localId, etc.
            // it returns a shallow copy with the _localId added
            if (optionObj._localId) {  // already have one?  return shallow copy
                return {...optionObj};
            }
            const localId = optionObj[this.field] ? `${this.field}_${optionObj[this.field]}` : `id_${optionObj.id}`;
            return {_localId: localId, ...optionObj};
        },
        setSelection(newValue) {
            // called from watcher on value prop
            console.log('in setSelection', newValue);
            // we'll always keep this.selection as an array, whether multiple=true or not - just adjust to incoming value being single object or array or null
            if (!newValue) {
                this.selection = [];
            }
            const newValueArray = Array.isArray(newValue) ? [...newValue] : [newValue];
            const valArray = newValueArray.map(valObj => this.makeLocalOption(valObj));
            //this.options = valArray;
            this.selection = valArray;
            // do we also need to add to this.options??? 
        },
        resetSelection() {
            this.selection = [];
        },
        clearAll() {
            this.resetSelection();
        },
        addNew(newValue) {
            // newValue is the string typed in... will be name or part_number - based on this.field
            // see notes above for the logic we use in making the unique keys 
            const newObjRaw = {};
            newObjRaw[this.field] = newValue;
            const newObj = this.makeLocalOption(newObjRaw);
            //const localId = this.makeLocalId({newValue: newValue});
            // we must manually check if this localId already exists... multiselect component doesn't know how we make new localIds, so it can't know on its own
            if (this.selectedLocalIds.includes(newObj._localId)) {
                // don't add it to options or selection... already there
                return;
            }            
            // make PK here or on backend?  backend... use missing PK to know it is new
            this.options.push(newObj);
            this.selection.push(newObj);
            // ALSO Need to call onInput() with the new selection as payload -- because normal @input event is not triggered when we update this.selection this way
            this.onInput(this.selection);
        },
        onInput(value) {
            console.log('from onInput', value);
            // handle the @input event from underlying component
            // the new values (all selections) will already be in payload... so just $emit up to parent from here also
            // should we strip off the _localId?  yes, cleaner that way
            // this is where we can either report up single object or array of objects, depending on multiple
            // just make a shallow copy of all selections, with extra _localId removed?  
            // const valueClone = cloneDeep(value);
            const cleanValue = value.map(s => {
                const { _localId, ...cleanObj } = s;
                console.log(_localId);
                return {...cleanObj};
            });
            this.$emit('input', cleanValue);
        },
    }
}
</script>

<style lang="scss">
/* You need to import the CSS from node_modules/vue-multiselect/dist/vue-multiselect.min.css - but you alraedy are doing this globally in project CSS */

.item-autocomplete-multiselect {

    &.multiselect--disabled {
        opacity: 1;  // by default it goes to 0.6, but we use disabled to show read-only things
        .multiselect__tags {
            // this is what actually controls the background color
            background-color: var(--gray-200);  // matches other formio and smartform inputs
        }
    }

    .multiselect__tags {
        display: flex;
        flex-wrap: wrap;

        .multiselect__tags-wrap {
            // built-in class around all tags - normally display: inline
            display: flex;
            flex-wrap: wrap;
            column-gap: 0.5rem;
            row-gap: 0.25rem;
            //padding-top: 6px;
            //padding-bottom: 6px;
            margin-bottom: 8px;  // gives space between tags and search input below them, equal to padding of overall autocomplete
        }
    }

    .item-autocomplete-tag {
        display: inline-flex;
        align-items: center;
        background-color: var(--gray-100);
        color: #000;
        border-radius: 1rem;
        border: 1px solid var(--gray-300);
        padding: 0.1rem;

        .tag-text {
            //font-size: 90%;
            font-weight: 500;
            padding-left: 0.25rem;
            padding-right: 0.25rem;
            padding-top: 2px;
            padding-bottom: 2px;
        }

        .tag-remove {
            background-color: transparent;
            border: 1px solid transparent;
            border-radius: 1rem;
            padding: 2px;

            &:hover {
                border-color: var(--gray-300);
                background-color: rgba(0,0,0,0.05);
            }
        }
    }

    .multiselect__option--highlight {
        background-color: var(--primary-100);
        color: initial;
    }

    .multiselect__content-wrapper {
        box-shadow: $box-shadow;
    }

}


</style>